import { Box, ThemeProvider, useMediaQuery, useTheme } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { clientTheme } from '@/v2/theme'
import { motion as motionTokens, prefersReducedMotion } from '@/v2/design-system'
import { SidebarV2, SIDEBAR_V2_COLLAPSED_WIDTH, SIDEBAR_V2_WIDTH } from '@/v2/layouts/SidebarV2'
import { TopContextBar } from '@/v2/layouts/TopContextBar'

/**
 * Cockpit base de GOVIA UI V2 — compone SidebarV2 + TopContextBar + área
 * de contenido. Se monta DENTRO de `<ProtectedRoute>` (igual que
 * `AppLayout` clásico) — reutiliza auth/permisos, no los reimplementa.
 * A diferencia de `AppLayout`, el área de contenido NO impone un
 * `max-width` — queda preparada para layouts full-width (mapa grande,
 * drawers, superficies de comando) que las fases siguientes construirán.
 *
 * UX-2.1 — anida `<ThemeProvider theme={clientTheme}>` (identidad visual
 * oficial de GOVIA Client Application), mismo patrón exacto que
 * `AppShellSuperAdmin` con `superAdminTheme` (sin un segundo
 * `<CssBaseline>` — un solo reset global basta, el ya montado en la raíz
 * por `AppProviders`; el fondo se fija explícitamente vía
 * `bgcolor: 'background.default'`, igual que `AppShellSuperAdmin`) —
 * nunca reemplaza el ThemeProvider raíz, así que V1 clásico y Super
 * Admin no cambian.
 */
export function AppShellV2() {
  return (
    <ThemeProvider theme={clientTheme}>
      <AppShellV2Content />
    </ThemeProvider>
  )
}

function AppShellV2Content() {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()
  const reducedMotion = prefersReducedMotion()

  const sidebarWidth = collapsed ? SIDEBAR_V2_COLLAPSED_WIDTH : SIDEBAR_V2_WIDTH

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <SidebarV2
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        collapsed={isDesktop && collapsed}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
      />

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          ml: isDesktop ? `${sidebarWidth}px` : 0,
          transition: `margin-left ${motionTokens.duration.base}ms ${motionTokens.easing.standard}`,
        }}
      >
        <TopContextBar onMenuClick={() => setMobileOpen(true)} />

        <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={reducedMotion ? undefined : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reducedMotion ? undefined : { opacity: 0, y: -8 }}
              transition={{ duration: motionTokens.duration.base / 1000, ease: motionTokens.easingArray.standard }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>
    </Box>
  )
}
